/**
 * Removes the flat studio backdrop from a product mockup.
 *
 * The backdrop colour is sampled from the image corners (mockups are exported
 * on white or a very light off-white), then a flood fill runs inward from the
 * borders clearing only pixels close to that colour. Because the fill is
 * contiguous, light-coloured book covers keep their artwork.
 */
export async function removeWhiteBackground(
  src: string,
  options: { tolerance?: number } = {},
): Promise<string> {
  // Kept tight on purpose: a wide tolerance bleeds into the cream/off-white
  // book covers and eats part of the artwork.
  const { tolerance = 12 } = options

  const image = await loadImage(src)

  const canvas = document.createElement('canvas')
  canvas.width = image.naturalWidth
  canvas.height = image.naturalHeight

  const context = canvas.getContext('2d', { willReadFrequently: true })
  if (!context) return src

  context.drawImage(image, 0, 0)

  const { width, height } = canvas
  const imageData = context.getImageData(0, 0, width, height)
  const pixels = imageData.data
  const visited = new Uint8Array(width * height)
  const queue: number[] = []

  const corners = [
    0,
    width - 1,
    (height - 1) * width,
    height * width - 1,
  ]

  let sampleR = 0
  let sampleG = 0
  let sampleB = 0
  corners.forEach((index) => {
    sampleR += pixels[index * 4]
    sampleG += pixels[index * 4 + 1]
    sampleB += pixels[index * 4 + 2]
  })
  sampleR /= corners.length
  sampleG /= corners.length
  sampleB /= corners.length

  // Only key out light backdrops — never a dark, intentional background.
  if (Math.min(sampleR, sampleG, sampleB) < 200) return src

  const isBackground = (index: number) => {
    const offset = index * 4
    if (pixels[offset + 3] === 0) return true
    const r = pixels[offset]
    const g = pixels[offset + 1]
    const b = pixels[offset + 2]
    if (Math.min(r, g, b) < 224) return false
    return (
      Math.abs(r - sampleR) <= tolerance &&
      Math.abs(g - sampleG) <= tolerance &&
      Math.abs(b - sampleB) <= tolerance
    )
  }

  const push = (index: number) => {
    if (visited[index]) return
    visited[index] = 1
    if (!isBackground(index)) return
    pixels[index * 4 + 3] = 0
    queue.push(index)
  }

  for (let x = 0; x < width; x += 1) {
    push(x)
    push((height - 1) * width + x)
  }
  for (let y = 0; y < height; y += 1) {
    push(y * width)
    push(y * width + width - 1)
  }

  while (queue.length > 0) {
    const index = queue.pop() as number
    const x = index % width
    const y = (index - x) / width

    if (x > 0) push(index - 1)
    if (x < width - 1) push(index + 1)
    if (y > 0) push(index - width)
    if (y < height - 1) push(index + width)
  }

  softenFringe(pixels, width, height)

  context.putImageData(imageData, 0, 0)
  return canvas.toDataURL('image/png')
}

/** Clears the light 1px halo the antialiased mockup edge leaves behind. */
function softenFringe(pixels: Uint8ClampedArray, width: number, height: number): void {
  const cleared: number[] = []

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const index = y * width + x
      const offset = index * 4
      if (pixels[offset + 3] === 0) continue
      if (Math.min(pixels[offset], pixels[offset + 1], pixels[offset + 2]) < 238) continue

      const hasTransparentNeighbour =
        (x > 0 && pixels[(index - 1) * 4 + 3] === 0) ||
        (x < width - 1 && pixels[(index + 1) * 4 + 3] === 0) ||
        (y > 0 && pixels[(index - width) * 4 + 3] === 0) ||
        (y < height - 1 && pixels[(index + width) * 4 + 3] === 0)

      if (hasTransparentNeighbour) cleared.push(offset)
    }
  }

  cleared.forEach((offset) => {
    pixels[offset + 3] = 0
  })
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image()
    image.crossOrigin = 'anonymous'
    image.onload = () => resolve(image)
    image.onerror = () => reject(new Error(`Could not load image: ${src}`))
    image.src = src
  })
}
